import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Cat, Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12">
      <Card className="w-full max-w-md p-8 rounded-2xl border-border/70 shadow-sm animate-fade-up">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
            <Cat className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-serif text-xl">AnnieHealth</span>
        </div>

        {sent ? (
          <div className="text-center py-4" data-testid="forgot-sent-message">
            <div className="mx-auto h-14 w-14 rounded-full bg-accent flex items-center justify-center mb-4">
              <MailCheck className="h-7 w-7 text-accent-foreground" />
            </div>
            <h2 className="font-serif text-2xl mb-2">Check your email</h2>
            <p className="text-muted-foreground text-sm mb-6">
              If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent a
              link to reset your password. The link expires in 1 hour.
            </p>
            <Link to="/login">
              <Button variant="outline" className="rounded-xl" data-testid="back-to-login-button">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to sign in
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-serif text-3xl mb-1">Forgot your password?</h2>
            <p className="text-muted-foreground text-sm mb-6">
              Enter your account email and we'll send you a link to set a new password.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="forgot-email-input" value={email}
                  onChange={(e) => setEmail(e.target.value)} required className="rounded-xl"
                  placeholder="you@example.com" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-xl" data-testid="forgot-submit-button">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send reset link
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-primary hover:underline" data-testid="back-to-login-link">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
